import "@/App.css";
import { useEffect } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

// Global axios interceptor: attach session token from localStorage to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardRouter from "@/pages/DashboardRouter";
import Dashboard from "@/pages/Dashboard";
import CocktailsPage from "@/pages/CocktailsPage";
import UploadPage from "@/pages/UploadPage";
import StaffPage from "@/pages/StaffPage";
import TestCreationPage from "@/pages/TestCreationPage";
import TestHistoryPage from "@/pages/TestHistoryPage";
import TakeTestPage from "@/pages/TakeTestPage";
import PracticeTestPage from "@/pages/PracticeTestPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import CompetitionsPage from "@/pages/CompetitionsPage";
import CompeteTestPage from "@/pages/CompeteTestPage";
import GroupCompeteTestPage from "@/pages/GroupCompeteTestPage";
import StudyPage from "@/pages/StudyPage";
import SetupAccountPage from "@/pages/SetupAccountPage";
import UserDashboard from "@/pages/UserDashboard";
import LibraryPage from "@/pages/LibraryPage";
import SetupPage from "@/pages/SetupPage";
import TargetedPracticePage from "@/pages/TargetedPracticePage";
import FlashSpecsPage from "@/pages/FlashSpecsPage";
import ChallengePage from "@/pages/ChallengePage";
import WinePage from "@/pages/WinePage";
import FoodPage from "@/pages/FoodPage";
import TournamentMatchPage from "@/pages/TournamentMatchPage";
import LandingPage from "@/pages/LandingPage";
import RootRedirect from "@/pages/RootRedirect";
import AdminGamificationPage from "@/pages/AdminGamificationPage";
import { Navigate } from "react-router-dom";

function AppRouter() {
  const location = useLocation();

  // Handle auth callback - check URL fragment for session_id
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<RootRedirect />} />
      {/* Legacy: marketing landing page is still reachable for direct links — but no longer rendered at "/" */}
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/take-test/:accessCode" element={<TakeTestPage />} />
      <Route path="/practice/:practiceCode" element={<PracticeTestPage />} />
      <Route path="/compete/:accessCode" element={<CompeteTestPage />} />
      <Route path="/group-compete/:accessCode" element={<GroupCompeteTestPage />} />
      <Route path="/tournament/:tournamentId/match/:matchId" element={<TournamentMatchPage />} />
      <Route path="/study/:testId" element={<StudyPage />} />
      <Route path="/flashspecs/study/:testId" element={<FlashSpecsPage />} />
      <Route path="/setup-account/:token" element={<SetupAccountPage />} />
      
      {/* Protected routes (admin + user) */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          {/* Smart dashboard — picks Admin or User view based on role */}
          <Route path="/dashboard" element={<DashboardRouter />} />
          <Route path="/cocktails" element={<CocktailsPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/create-test" element={<TestCreationPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/wines" element={<WinePage />} />
          <Route path="/food" element={<FoodPage />} />
          <Route path="/competitions" element={<CompetitionsPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/admin/gamification" element={<AdminGamificationPage />} />
          <Route path="/history" element={<TestHistoryPage />} />
          {/* User routes (new /flashspecs/* namespace) */}
          <Route path="/flashspecs" element={<FlashSpecsPage />} />
          <Route path="/flashspecs/dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/flashspecs/challenge" element={<ChallengePage />} />
          <Route path="/flashspecs/practice-weak" element={<TargetedPracticePage />} />
          {/* Backwards-compat redirects (preserve old bookmarks) */}
          <Route path="/my-dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/challenge" element={<Navigate to="/flashspecs/challenge" replace />} />
          <Route path="/practice-weak" element={<Navigate to="/flashspecs/practice-weak" replace />} />
        </Route>
      </Route>
      
      {/* Root fallback handled by LandingPage route above */}
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const kill = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.parentNode.removeChild(el);
      document.querySelectorAll('a[href*="emergent"]').forEach(a => {
        if (a.textContent.includes('Emergent')) a.parentNode.removeChild(a);
      });
    };
    kill();
    const iv = setInterval(kill, 300);
    const obs = new MutationObserver(kill);
    obs.observe(document.body, { childList: true, subtree: true });

    // Register the PWA service worker. We previously only did this in production
    // to avoid CRA's dev HMR fighting with the SW, but that meant the install
    // prompt was impossible to test on preview (Chrome refuses to fire
    // `beforeinstallprompt` without a registered SW). Now we register unless we
    // detect a CRA hot-reload dev server explicitly opted out of it.
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker
          .register("/service-worker.js", { scope: "/" })
          .catch(() => {});
      });
    }

    return () => { clearInterval(iv); obs.disconnect(); };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <div className="noise-overlay" />
    </div>
  );
}

export default App;
